import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from './dialog';
import { Text } from './text';
import { Button } from './button';
import { Heading } from './heading';
import { 
  getActiveConversation, 
  createNewConversation, 
  sendChatMessage, 
  createUserMessage,
  formatTimestamp,
  generateContractId 
} from '../api/contractChat';

export function ContractChatDialog({
  open,
  onClose,
  contract, // { linkId, fileUrl, tituloContrato, ... }
}) {
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [message, setMessage] = useState('');
  const [hasActiveConversation, setHasActiveConversation] = useState(false);
  const [, setChatId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generate contract ID from linkId and fileUrl
  const contractId = contract ? generateContractId(contract.linkId, contract.fileUrl) : null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open && contractId) {
      loadActiveConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const loadActiveConversation = async () => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const response = await getActiveConversation(contractId);
      
      if (response.hasActiveConversation) {
        setConversation(response.conversation || []);
        setChatId(response.chatId);
        setHasActiveConversation(true);
      } else {
        setConversation([]);
        setChatId(null);
        setHasActiveConversation(false);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      setError('Error al cargar la conversación: ' + error.message);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleNewConversation = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await createNewConversation(contractId);
      setConversation([]);
      setChatId(null);
      setHasActiveConversation(false);
    } catch (error) {
      console.error('Error creating new conversation:', error);
      setError('Error al crear nueva conversación: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = createUserMessage(message.trim());
    const updatedConversation = [...conversation, userMessage];
    
    setConversation(updatedConversation);
    setMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendChatMessage(contractId, updatedConversation);
      
      // Update conversation with the complete response
      setConversation(response.conversation || updatedConversation);
      setChatId(response.chatId);
      setHasActiveConversation(true);
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Error al enviar mensaje: ' + error.message);
      // Remove the user message if sending failed
      setConversation(conversation);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (msg, index) => {
    const isUser = msg.type === 'user';

    return (
      <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-[80%] p-3 rounded-lg ${
          isUser 
            ? 'bg-purple-600 text-white' 
            : 'bg-zinc-100 text-zinc-900'
        }`}>
          <div className="whitespace-pre-wrap break-words">
            {msg.content}
          </div>
          
          {/* Show structured recommendation if available (only for assistant messages) */}
          {!isUser && msg.structured_recommendation && (
            <div className="mt-4 p-3 bg-white rounded border border-zinc-200">
              <div className="text-sm font-medium text-zinc-900 mb-2">
                📊 Análisis Estructurado
              </div>
              
              {/* Recommended Afianzadora */}
              {msg.structured_recommendation.recommended_afianzadora && (
                <div className="mb-3 p-2 bg-green-50 rounded">
                  <div className="text-xs font-medium text-green-800 mb-1">
                    🏆 Afianzadora Recomendada
                  </div>
                  <div className="text-sm text-green-900">
                    {msg.structured_recommendation.recommended_afianzadora}
                  </div>
                </div>
              )}

              {/* Afianzadoras Analysis */}
              {msg.structured_recommendation.afianzadoras_analysis && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-zinc-700 mb-2">
                    📈 Análisis de Afianzadoras
                  </div>
                  <div className="space-y-2">
                    {msg.structured_recommendation.afianzadoras_analysis.slice(0, 3).map((afianzadora, idx) => (
                      <div key={idx} className="text-xs p-2 bg-zinc-50 rounded">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">#{afianzadora.rank} {afianzadora.name}</span>
                          <span className="text-purple-600 font-medium">{afianzadora.score}/10</span>
                        </div>
                        <div className="text-zinc-600">{afianzadora.suitability}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Steps */}
              {msg.structured_recommendation.next_steps && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-zinc-700 mb-1">
                    ✅ Próximos Pasos
                  </div>
                  <ul className="text-xs text-zinc-600 space-y-1">
                    {msg.structured_recommendation.next_steps.slice(0, 3).map((step, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Sources */}
          {!isUser && msg.sources && msg.sources.length > 0 && (
            <div className="mt-2 pt-2 border-t border-zinc-300">
              <div className="text-xs opacity-75 mb-1">Fuentes consultadas:</div>
              <div className="space-y-1">
                {msg.sources.slice(0, 2).map((source, idx) => (
                  <div key={idx} className="text-xs opacity-75">
                    📄 {source.company_id} - {source.document_name}
                    {source.pages && ` (págs. ${source.pages.join(', ')})`}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`text-xs mt-2 ${isUser ? 'text-purple-200' : 'text-zinc-500'}`}>
            {formatTimestamp(msg.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeMessage = () => (
    <div className="text-center py-8">
      <div className="mb-4">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🤖</span>
        </div>
        <Heading level={3} className="text-zinc-900 mb-2">
          Chat de Recomendaciones de Afianzadoras
        </Heading>
        <Text className="text-zinc-600 mb-4">
          Obtén recomendaciones inteligentes de afianzadoras para tu contrato
        </Text>
        <Text className="text-sm text-zinc-500">
          Contrato: {contract?.tituloContrato?.value || 'Sin título'}
        </Text>
      </div>
      
      <div className="bg-zinc-50 rounded-lg p-4 text-left">
        <Text className="text-sm font-medium text-zinc-900 mb-2">
          Ejemplos de preguntas:
        </Text>
        <ul className="text-sm text-zinc-600 space-y-1">
          <li>• ¿Qué afianzadora me recomiendas para este contrato?</li>
          <li>• ¿Cuáles son las mejores opciones de fianzas?</li>
          <li>• ¿Qué criterios debo considerar para seleccionar una afianzadora?</li>
          <li>• Compara las afianzadoras disponibles</li>
        </ul>
      </div>
    </div>
  );

  if (!contract) return null;

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <div className="flex flex-col h-[80vh]">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <div className="flex-1">
            <Heading level={2} className="text-zinc-900">
              Chat de Afianzadoras
            </Heading>
            <Text className="text-sm text-zinc-600 mt-1">
              {contract.tituloContrato?.value || 'Contrato'} • ID: {contractId}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveConversation && (
              <Button
                color="white"
                size="sm"
                onClick={handleNewConversation}
                disabled={isLoading}
              >
                Nueva Conversación
              </Button>
            )}
            <Button color="white" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <Text>Cargando conversación...</Text>
              </div>
            </div>
          ) : conversation.length === 0 ? (
            renderWelcomeMessage()
          ) : (
            <>
              {conversation.map((msg, index) => renderMessage(msg, index))}
              {isLoading && (
                <div className="flex justify-start mb-4">
                  <div className="bg-zinc-100 p-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                      <span className="text-sm text-zinc-600">Analizando...</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        {error && (
          <div className="px-6 py-2">
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <Text className="text-sm text-red-700">{error}</Text>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-zinc-200 p-6">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Escribe tu pregunta sobre afianzadoras..."
              className="flex-1 resize-none border border-zinc-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={2}
              disabled={isLoading}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || isLoading}
              className="self-end"
            >
              {isLoading ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}